import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { FugueContactForm } from '@/components/FugueContactForm'
import { PortfolioCard } from '@/components/PortfolioCard'
import { fugueLabProjects } from '@/data/portfolio'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Check, ArrowRight } from 'lucide-react'
import type { AudienceSegment } from '@/data/whoWeServe'

export function AudiencePageTemplate({ segment }: { segment: AudienceSegment }) {
  const url = `/who-we-help/${segment.slug}`
  const breadcrumbs = generateBreadcrumbs(url, breadcrumbLabels)

  useSEO({
    title: segment.seoTitle,
    description: segment.seoDescription,
    url,
    canonical: url,
    breadcrumbs,
    schema: createServiceSchema({
      name: segment.seoTitle,
      description: segment.seoDescription,
      url,
    }),
  })

  const heroRef = useHeroReveal<HTMLElement>(0.2)
  const painRef = useStaggeredGrid<HTMLDivElement>('.pain-card', { stagger: 0.08 })
  const buildRef = useStaggeredGrid<HTMLDivElement>('.build-item', { stagger: 0.08 })
  const proofRef = useScrollReveal<HTMLElement>('.proof-content')
  const portfolioRef = useStaggeredGrid<HTMLDivElement>('.portfolio-item', { stagger: 0.1 })
  const faqRef = useScrollReveal<HTMLElement>('.faq-item', { stagger: 0.08 })
  const ctaRef = useScrollReveal<HTMLElement>('.cta-content')

  const projects = fugueLabProjects
    .filter((project) => project.clientTypes?.some((type) => segment.portfolioTypes.includes(type)))
    .slice(0, 3)

  return (
    <div className="mx-auto max-w-6xl space-y-24 px-4 py-16">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <p className="text-primary text-sm font-semibold tracking-wide uppercase">{segment.name}</p>
        <h1 className="text-fluid-h1">{segment.heroHeadline}</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">{segment.heroBody}</p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call <ArrowRight className="ml-1 h-5 w-5" />
              </Button>
            </motion.div>
          </a>
        </div>
      </section>

      {/* Pain points */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Sound familiar?</h2>
        <div ref={painRef} className="grid gap-6 md:grid-cols-2">
          {segment.painPoints.map((pain) => (
            <div
              key={pain.title}
              className="pain-card border-border/50 rounded-2xl border p-6 transition-all duration-300 hover:shadow-lg"
            >
              <h3 className="font-bold">{pain.title}</h3>
              <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{pain.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What we build */}
      <section className="space-y-8">
        <h2 className="text-fluid-h2 text-center">What we build for you</h2>
        <div ref={buildRef} className="mx-auto grid max-w-4xl gap-6 md:grid-cols-2">
          {segment.whatWeBuild.map((item) => (
            <div key={item.title} className="build-item flex items-start gap-3">
              <div className="bg-primary/10 text-primary mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg">
                <Check className="h-4 w-4" strokeWidth={2.5} />
              </div>
              <div>
                <h3 className="font-bold">{item.title}</h3>
                <p className="text-muted-foreground mt-1 text-sm leading-relaxed">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Case studies */}
      {segment.caseStudies.length > 0 && (
        <section ref={proofRef} className="proof-content mx-auto max-w-3xl space-y-6 text-center">
          <h2 className="text-fluid-h3">From the case files</h2>
          <div className="space-y-3">
            {segment.caseStudies.map((caseStudy) => (
              <Link
                key={caseStudy.href}
                to={caseStudy.href}
                className="border-border/60 hover:border-primary/40 group flex items-center justify-between gap-4 rounded-xl border p-5 text-left transition"
              >
                <span className="font-medium">{caseStudy.label}</span>
                <ArrowRight className="text-primary h-4 w-4 flex-shrink-0 transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Portfolio proof */}
      {projects.length > 0 && (
        <section className="space-y-8">
          <h2 className="text-fluid-h2 text-center">Recent work</h2>
          <div ref={portfolioRef} className="grid gap-6 md:grid-cols-3">
            {projects.map((project) => (
              <div key={project.slug} className="portfolio-item">
                <PortfolioCard {...project} />
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/portfolio" className="text-primary inline-flex items-center gap-2 font-medium hover:underline">
              See the full portfolio <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section ref={faqRef} className="mx-auto max-w-3xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Questions that come up</h2>
        <div className="space-y-6">
          {segment.faqs.map((faq) => (
            <div key={faq.question} className="faq-item border-border/50 rounded-xl border p-6">
              <h3 className="font-bold">{faq.question}</h3>
              <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{faq.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section
        ref={ctaRef}
        className="cta-content from-primary/10 via-primary/5 border-primary/20 space-y-6 rounded-2xl border bg-gradient-to-br to-transparent p-8 text-center md:p-12"
      >
        <h2 className="text-fluid-h3">{segment.ctaHeading}</h2>
        <p className="text-muted-foreground mx-auto max-w-2xl">{segment.ctaBody}</p>
        <motion.a
          href="/book"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary inline-flex cursor-pointer items-center gap-2 font-medium hover:underline"
          whileHover={{ x: 4 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        >
          Book a Free Strategy Call <ArrowRight className="h-4 w-4" />
        </motion.a>
      </section>

      <FugueContactForm />
    </div>
  )
}
